import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Allergen, Product } from '@reims-local/types';
import { ALLERGENS } from '@reims-local/types';
import { shopApi } from '../lib/api';

const ALLERGEN_LABELS: Record<Allergen, string> = {
  gluten: 'Gluten', crustaces: 'Crustacés', oeufs: 'Œufs', poissons: 'Poissons',
  arachides: 'Arachides', soja: 'Soja', lait: 'Lait', fruits_a_coque: 'Fruits à coque',
  celeri: 'Céleri', moutarde: 'Moutarde', sesame: 'Sésame', sulfites: 'Sulfites',
  lupin: 'Lupin', mollusques: 'Mollusques',
};

export function ProductEditor({ productId }: { productId?: string }) {
  const navigate = useNavigate();
  const isNew = !productId;
  const [product, setProduct] = useState<Partial<Product>>({
    name: '',
    description: '',
    price_cents: 0,
    stock: 0,
    stock_alert_threshold: 3,
    is_available: true,
    is_perishable: false,
    is_made_to_order: false,
    allergens: [],
    sort_order: 0,
  });
  const [priceInput, setPriceInput] = useState('');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!productId) return;
    shopApi.listProducts().then((res) => {
      const found = res.data.find((p) => p.id === productId);
      if (found) {
        setProduct(found);
        setPriceInput((found.price_cents / 100).toString());
      } else {
        setError('Produit introuvable.');
      }
      setLoading(false);
    }).catch((err) => {
      setError((err as Error).message);
      setLoading(false);
    });
  }, [productId]);

  function setField<K extends keyof Product>(k: K, v: Product[K]) {
    setProduct({ ...product, [k]: v });
  }

  function toggleAllergen(a: Allergen) {
    const cur = (product.allergens ?? []) as Allergen[];
    setField('allergens', cur.includes(a) ? cur.filter((x) => x !== a) : [...cur, a]);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const parsed = Math.round(parseFloat(priceInput.replace(',', '.')) * 100);
      if (!Number.isFinite(parsed) || parsed < 0) {
        setError('Prix invalide.');
        setSaving(false);
        return;
      }
      const payload: Partial<Product> = {
        name: product.name ?? '',
        description: product.description ?? null,
        price_cents: parsed,
        category: product.category ?? null,
        stock: product.stock ?? 0,
        stock_alert_threshold: product.stock_alert_threshold ?? 3,
        is_available: product.is_available ?? true,
        is_perishable: product.is_perishable ?? false,
        is_made_to_order: product.is_made_to_order ?? false,
        allergens: product.allergens ?? [],
        sort_order: product.sort_order ?? 0,
      };
      if (isNew) {
        const res = await shopApi.createProduct(payload);
        navigate(`/products/${res.data.id}`);
      } else if (productId) {
        const res = await shopApi.updateProduct(productId, payload);
        setProduct(res.data);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handlePhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f || !productId) return;
    setUploading(true);
    setError(null);
    try {
      const res = await shopApi.uploadPhoto(productId, f);
      setField('photo_url', res.data.photo_url);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  if (loading) return <p>Chargement…</p>;

  return (
    <>
      <Link to="/products">← Retour au catalogue</Link>
      <h2 style={{ marginTop: 12 }}>{isNew ? 'Nouveau produit' : 'Éditer le produit'}</h2>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={save}>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Informations</h3>
          <div className="form-row">
            <label>Nom *</label>
            <input required value={product.name ?? ''} onChange={(e) => setField('name', e.target.value)} />
          </div>
          <div className="form-row">
            <label>Description</label>
            <textarea rows={4} value={product.description ?? ''} onChange={(e) => setField('description', e.target.value)} />
          </div>
          <div className="grid-2">
            <div className="form-row">
              <label>Catégorie</label>
              <input value={product.category ?? ''} onChange={(e) => setField('category', e.target.value)} />
            </div>
            <div className="form-row">
              <label>Prix (€) *</label>
              <input
                required
                type="text"
                inputMode="decimal"
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                placeholder="9,50"
              />
            </div>
          </div>
        </div>

        <div className="card">
          <h3 style={{ marginTop: 0 }}>Stock</h3>
          <div className="form-row-inline" style={{ marginBottom: 16 }}>
            <input id="mto" type="checkbox" style={{ width: 'auto' }} checked={product.is_made_to_order ?? false} onChange={(e) => setField('is_made_to_order', e.target.checked)} />
            <label htmlFor="mto">Sur commande (pas de stock — confirmation manuelle)</label>
          </div>
          <div className="form-row-inline" style={{ marginBottom: 16 }}>
            <input id="per" type="checkbox" style={{ width: 'auto' }} checked={product.is_perishable ?? false} onChange={(e) => setField('is_perishable', e.target.checked)} />
            <label htmlFor="per">Produit périssable (stock remis à zéro chaque matin)</label>
          </div>
          {!product.is_made_to_order && (
            <div className="grid-2">
              <div className="form-row">
                <label>Stock disponible</label>
                <input type="number" min={0} value={product.stock ?? 0} onChange={(e) => setField('stock', Number(e.target.value))} />
              </div>
              <div className="form-row">
                <label>Seuil d’alerte (stock bas)</label>
                <input type="number" min={0} value={product.stock_alert_threshold ?? 3} onChange={(e) => setField('stock_alert_threshold', Number(e.target.value))} />
              </div>
            </div>
          )}
          <div className="form-row-inline">
            <input id="avail" type="checkbox" style={{ width: 'auto' }} checked={product.is_available ?? true} onChange={(e) => setField('is_available', e.target.checked)} />
            <label htmlFor="avail">Produit publié (visible par les clients)</label>
          </div>
        </div>

        <div className="card">
          <h3 style={{ marginTop: 0 }}>Allergènes</h3>
          <p className="muted">Cochez les allergènes présents dans ce produit (INCO EU 1169/2011).</p>
          <div className="pill-group">
            {ALLERGENS.map((a) => {
              const active = (product.allergens ?? []).includes(a);
              return (
                <label key={a} className={active ? 'active' : ''}>
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() => toggleAllergen(a)}
                  />
                  {ALLERGEN_LABELS[a]}
                </label>
              );
            })}
          </div>
        </div>

        {!isNew && (
          <div className="card">
            <h3 style={{ marginTop: 0 }}>Photo</h3>
            <div style={{ maxWidth: 240, marginBottom: 12 }}>
              <div className="product-photo" style={{ backgroundImage: product.photo_url ? `url(${product.photo_url})` : undefined }} />
            </div>
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePhoto} disabled={uploading} />
            <div className="muted" style={{ marginTop: 4 }}>
              JPG, PNG ou WebP — max 5 Mo. {uploading && 'Envoi en cours…'}
            </div>
          </div>
        )}

        <button className="primary" type="submit" disabled={saving}>
          {saving ? 'Enregistrement…' : isNew ? 'Créer le produit' : 'Enregistrer'}
        </button>
      </form>
    </>
  );
}
